import React, { useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Timeline } from 'antd';
import { DateTime } from 'luxon';
import {
  MailOutlined,
  FileSearchOutlined,
  FilePdfOutlined,
  FormOutlined,
  LoadingOutlined,
  FlagTwoTone,
  MobileOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  TagOutlined,
  EditOutlined,
  EditFilled,
  ProjectFilled,
  CloudSyncOutlined,
  DollarOutlined,
  StopOutlined,
  GoldOutlined,
  BranchesOutlined,
  RetweetOutlined,
  GroupOutlined,
  RotateRightOutlined,
  IssuesCloseOutlined,
  ClockCircleOutlined,
  ProfileOutlined,
  ToolOutlined,
  FileTextOutlined,
  UserOutlined,
  TableOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { resubmitEmail, cancelEmailReminder } from '../state/forms.actions';
import {
  getIdMap,
  isNullOrUndefined,
  toTitleCase,
  formatListWithAnd,
} from '../../helpers/helpers';
import Step from '../../common/containers/TimelineStep';
import TimelineStepText from '../../common/containers/TimelineStepText';
import FormUpdateStep from './FormUpdateStep';
import { currencyFormatter } from '../../helpers/inputParsers';
import Colors from '../../constants/Colors';

import FormTimelineCardLink from './FormTimelineCardLink';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';
import {
  EmailBlockedTimelineText,
  getEmailBlockFromMetadata,
} from './formEmailBlockTimelineHelpers';

const boardPrefix = (board) => board ? `${board}:` : '';
const statusTitle = (board, status) => bold(`${boardPrefix(board)}${status}`);
const bold = (text) => <span style={{ fontFamily: 'roboto-regular'}}>{text} </span>;
const Ontraccr = bold('Ontraccr');
const pluralize = (count, singular, plural = `${singular}s`) => (count === 1 ? singular : plural);

export const parseMetadata = (metadata) => {
  try {
    return JSON.parse(metadata || '{}');
  } catch (err) {
    return {};
  }
};

const formatTimelineDate = (ms) => (
  ms ? DateTime.fromMillis(ms).toLocaleString(DateTime.DATETIME_MED) : null
);

const formatUserIds = ({
  userIds,
  userMap,
  defaultValue = () => null,
}) => (
  userIds.map((userId) => {
    if (!(userId in userMap)) return defaultValue(userId);
    return (
      <>
        {`- ${userMap[userId].name}`}
        <br />
      </>
    );
  })
);
const parseUsers = ({
  nodeMap,
  stepId,
  userMap,
  formResponseMap,
  metadataUsers,
  defaultValue = () => null,
}) => {
  const {
    [stepId]: {
      data: {
        users = [],
      } = {},
    } = {},
  } = nodeMap;

  let userIds = [];
  // Prior to 2.6.1
  // Users were not stored in the step metadata
  if (metadataUsers) {
    userIds = metadataUsers;
  } else {
    users.forEach((userIdOrFieldId) => {
      if (typeof userIdOrFieldId === 'string' && userIdOrFieldId.startsWith('field-')) {
        // References a field
        const {
          [userIdOrFieldId]: {
            values: responseUserIds = [],
          } = {},
        } = formResponseMap;
        userIds = userIds.concat(responseUserIds.map((r) => r.id));
      } else {
        userIds.push(userIdOrFieldId);
      }
    });
  }

  return formatUserIds({ userIds, userMap, defaultValue });
};

const sentMessage = ({ caller, names, text }) => (
  <div>
    {caller || Ontraccr}
    {text}
    <br />
    {names}
  </div>
);

const getEmailList = (email, order) => (
  <>
    {`- ${order ? `(${order}) ` : ''}${email}`}
    <br />
  </>
);


const renderEmailBlockedTimeline = ({
  asStep = true,
  stepKey,
  title,
  Icon,
  timestamp,
  reason,
  textKind = 'default',
}) => {
  const text = <EmailBlockedTimelineText reason={reason} kind={textKind} />;
  if (!asStep) {
    return (
      <div style={{ marginTop: 8, color: EMAIL_BLOCK_TIMELINE_COLOR }}>
        {text}
      </div>
    );
  }
  return (
    <Step
      key={stepKey}
      Icon={Icon}
      title={title}
      color={'#d48806'}
      textColor={'#d48806'}
      text={text}
      timestamp={timestamp}
    />
  );
};

const PendingView = ({ isFinished }) => !isFinished
  ? <div style={{ fontWeight: 500 }}>Workflow In Progress</div> : null;
const PendingDot = ({ isFinished }) => !isFinished
  ? <LoadingOutlined style={{ color: 'black' }} /> : null;
const CompleteStep = ({ isFinished }) => (
  isFinished
  ? (
    <Timeline.Item
      dot={<FlagTwoTone twoToneColor="forestgreen" style={{ width: 5, height: 5 }} />}
      prefixCls="ot-timeline"
    >
      <div style={{ fontWeight: 500 }}>Workflow Complete</div>
    </Timeline.Item>
  )
  : null
);

export default function FormTimeline({
  userName,
  assignedUserNames = [],
  state: formState,
  templateSchema = {},
  steps = [],
  approvals = [],
  createdAt = 0,
  userMap = {},
  sections = [],
  formId = '',
  isFinished,
  onCardLinkClick,
  formType,
}) {
  const {
    workflow: {
      nodes = [],
    } = {},
    isExternalForm,
  } = templateSchema;
  const nodeMap = getIdMap(nodes);
  const dispatch = useDispatch();

  const templates = useSelector((state) => state.forms.templates);
  const positionNames = useSelector((state) => state.settings.positionNames);
  const formStatuses = useSelector((state) => state.forms.statuses);
  const projects = useSelector((state) => state.projects.projects);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const [loadingStep, setLoadingStep] = useState();

  const onEmailSubmit = useCallback(({
    id, stepId, sent, stepKey,
  }) => async () => {
    if (sent || !dispatch) return;
    setLoadingStep(stepKey);
    await dispatch(resubmitEmail({ formId, stepId, id }));
    setLoadingStep();
  }, [dispatch, formId]);

  const [cancellingSchedule, setCancellingSchedule] = useState(null);

  const getCancelledTitle = (reason, cancellerName) => {
    if (reason === 'end_date') return 'Cancelled - Reached End Date';
    if (reason === 'workflow_complete') return 'Cancelled - Workflow Complete';
    if (reason === 'error') return 'Cancelled - Error';
    return cancellerName ? `Cancelled by ${cancellerName}` : 'Cancelled';
  };

  const onCancelEmailReminder = useCallback((scheduleIdToCancel) => async () => {
    if (!scheduleIdToCancel) return;
    setCancellingSchedule(scheduleIdToCancel);
    await dispatch(cancelEmailReminder({ formId, scheduleId: scheduleIdToCancel }));
    setCancellingSchedule(null);
  }, [formId]);

  const positionIdMap = useMemo(() => getIdMap(positionNames), [positionNames]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const sortedSteps = useMemo(() => (
    steps
      .concat(
        approvals.map((approval) => ({
          ...approval,
          name: approval.state === 'approved' ? 'approval-action' : 'rejection-action',
        })),
      )
      .sort((a, b) => a.timestamp - b.timestamp)
  ), [approvals, steps]);

  const formResponseMap = useMemo(() => {
    const responseMap = {};
    sections.forEach(({ fields = [] }) => {
      fields.forEach((field) => {
        responseMap[field.id] = field.response;
      });
    });
    return responseMap;
  }, [sections]);

  const parseStep = useCallback(({
    name,
    timestamp,
    userId,
    stepId,
    metadata,
    id: workflowStepId,
    note,
  }, index) => {
    const jsonMetadata = parseMetadata(metadata);
    switch (name) {
      case 'assign': {
        const { type, userIds = [], assignerId } = jsonMetadata ?? {};
        const title = type === 'formTrigger' && !assignerId
          ? 'Workflow Trigger'
          : 'Form Assigned';
        let prefix = '';
        if (type === 'clockIn') {
          prefix = 'Clock In ';
        } else if (type === 'clockOut') {
          prefix = 'Clock Out ';
        } else if (type === 'switch') {
          prefix = 'Switch ';
        } else if (type === 'import') {
          prefix = 'Imported ';
        } else if (type === 'event') {
          prefix = 'Event ';
        } else if (type === 'manual') {
          prefix = 'Manual ';
        }
        return (
          <Timeline.Item
            color="black"
            key={stepId}
          >
            <TimelineStepText
              title={`${prefix}${title}`}
              timestamp={timestamp}
              text={
                sentMessage({
                  names: formatUserIds({
                    userIds,
                    userMap,
                    defaultValue: () => 'A deleted user',
                  }),
                  text: 'assigned the form to: ',
                  caller: assignerId ? `${userMap[assignerId]?.name} ` : null,
                })
              }
            />
          </Timeline.Item>
        );
      }
      case 'resubmit':
      case 'submit':
      case 'formEditSave': {
        const { userId: metadataUserId, isOffline } = jsonMetadata;
        const {
          [metadataUserId]: { name: submitter = 'Unknown User' } = {},
        } = userMap;
        let actionType = 'Submission';
        let verb = 'submitted';
        if (name === 'resubmit') {
          actionType = 'Resubmitted';
          verb = 'resubmitted';

          if (!isNullOrUndefined(jsonMetadata?.clearData)) {
            verb = jsonMetadata.clearData
              ? 'resubmitted and cleared the data in'
              : 'resubmitted and did not clear the data in';
          }
        } else if (name === 'formEditSave') {
          actionType = 'Data Updated';
          verb = 'updated the data in ';
        }
        const submitterName = isExternalForm ? 'External User' : submitter;
        return (
          <Step
            key={index}
            Icon={FormOutlined}
            title={`Form ${actionType}${isOffline ? ' (Offline)' : ''}`}
            text={
              isOffline
                ? `${submitterName} ${verb} the form offline`
                : `${submitterName} ${verb} the form`
            }
            timestamp={timestamp}
          />
        );
      }
      case 'poClose': {
        const { userId: metadataUserId } = jsonMetadata;
        const {
          [metadataUserId]: { name: submitter = 'Unknown User' } = {},
        } = userMap;
        const isSubContract = formType === 'Sub-Contract';
        const payableLabel = isSubContract ? 'sub-contract' : 'PO';
        return (
          <Step
            key={index}
            Icon={IssuesCloseOutlined}
            title={isSubContract ? 'Sub-Contract Closed' : 'PO Closed'}
            text={`${submitter} closed the ${payableLabel} form. Final changes may be viewed using the form snapshots feature`}
            timestamp={timestamp}
          />
        );
      }
      case 'approval-action': {
        const userText = userId in userMap ? ` by ${userMap[userId].name}` : '';
        return (
          <Step
            key={index}
            Icon={CheckCircleOutlined}
            color="green"
            title={`Form was approved${userText}`}
            timestamp={timestamp}
          />
        );
      }
      case 'rejection-action': {
        const userText = userId in userMap ? ` by ${userMap[userId].name}` : '';
        return (
          <Step
            key={index}
            Icon={CloseCircleOutlined}
            color="red"
            title={`Form was rejected${userText}`}
            timestamp={timestamp}
            note={note}
            text="Rejected"
          />
        );
      }
      case 'approval': {
        const userNames = parseUsers({
          nodeMap,
          stepId,
          userMap,
          formResponseMap,
          defaultValue: (positionId) => {
            if (!(positionId in positionIdMap)) return null;
            return <>
              - All {positionIdMap[positionId].name} users
              <br />
            </>;
          },
          metadataUsers: jsonMetadata.userIds,
        });
        return (
          <Step
            key={index}
            Icon={FileSearchOutlined}
            title="Form was sent for approval"
            text={
              sentMessage({
                text: 'sent the form to the following users for approval:',
                names: userNames,
              })
            }
            timestamp={timestamp}
          />
        );
      }
      case 'pdf': {
        const {
          [stepId]: {
            data: {
              exportLocation,
            } = {},
          } = {},
        } = nodeMap;
        const {
          locations,
          locations: stepLocations = [jsonMetadata.exportLocation ?? exportLocation],
          failed,
          pending,
        } = jsonMetadata;
        let title = 'Form was saved as a PDF';
        if (pending) {
          title = 'Form PDF save is pending';
        } else if (failed && !locations?.length) {
          title = 'No PDF export location set';
        } else if (failed) {
          title = 'Failed to generate form PDF';
        }
        let color = failed ? 'red' : 'black';
        let textColor = failed ? 'red' : null;
        if (pending) {
          color = 'gray';
          textColor = 'gray';
        }
        return (
          <Step
            key={index}
            Icon={FilePdfOutlined}
            title={title}
            color={color}
            textColor={textColor}
            text={failed || pending ? null : (
              <div>
                {Ontraccr}
                saved the form as a PDF
                { stepLocations.map((stepLocation) => (
                  <>
                    <br />
                    { `\n - in ${stepLocation}`}
                  </>
                ))}
              </div>
            )}
            timestamp={timestamp}
          />
        );
      }
      case 'push': {
        const userNames = parseUsers({ nodeMap, stepId, userMap });
        return (
          <Step
            key={index}
            Icon={MobileOutlined}
            title="Users were notifed in the mobile app"
            text={
              sentMessage({
                text: 'notified the following users on the mobile app:',
                names: userNames,
              })
            }
            timestamp={timestamp}
          />
        );
      }
      case 'emailResubmit':
      case 'email': {
        const isResubmit = name === 'emailResubmit';
        const {
          blocked: emailBlocked,
          reason: emailBlockReason,
        } = getEmailBlockFromMetadata(jsonMetadata);

        let emailList = [];
        let ccList = [];
        let bccList = [];
        let sent = false;
        let didFail = false;
        let pending = false;
        let isScheduledReminder = false;
        let scheduleId = null;
        let scheduledAt = null;
        let scheduleCancelled = false;
        let cancelReason = null;
        let cancelledByUserId = null;
        let errorMessage = null;
        try {
          const defaultMeta = metadata || '{}';
          const {
            emails = [],
            bcc = [],
            cc = [],
            resubmitted = false,
            failed = false,
            pending: isPending = false,
            scheduledReminder = false,
            scheduleId: metaScheduleId = null,
            scheduledAt: metaScheduledAt = null,
            scheduleCancelled: metaScheduleCancelled = false,
            cancelReason: metaCancelReason = null,
            cancelledByUserId: metaCancelledByUserId = null,
            errorMessage: metaErrorMessage = null,
          } = JSON.parse(defaultMeta);
          emailList = emails;
          ccList = cc;
          bccList = bcc;
          sent = resubmitted;
          didFail = failed;
          pending = isPending;
          isScheduledReminder = scheduledReminder;
          scheduleId = metaScheduleId;
          scheduledAt = metaScheduledAt;
          scheduleCancelled = metaScheduleCancelled;
          cancelReason = metaCancelReason;
          cancelledByUserId = metaCancelledByUserId;
          errorMessage = metaErrorMessage;
        } catch (err) {
          // Do nothing
        }
        const title = sent ? '✓' : 'Resend Email';
        const stepKey = `${stepId}-${timestamp}`;

        if (emailBlocked) {
          const blockedTitle = isScheduledReminder
            ? 'Scheduled reminder email was not sent'
            : (isResubmit
              ? 'Form email was not resent'
              : 'Form email was not sent');
          return renderEmailBlockedTimeline({
            stepKey,
            title: blockedTitle,
            Icon: isScheduledReminder ? CalendarOutlined : MailOutlined,
            timestamp,
            reason: emailBlockReason,
          });
        }

        const failVerb = isResubmit ? 'resend' : 'send';
        const successVerb = isResubmit ? 'resent' : 'sent';

        let stepTitle;
        if (isScheduledReminder && !emailList?.length && scheduledAt) {
          stepTitle = 'Scheduled email reminder created';
        } else if (isScheduledReminder) {
          stepTitle = didFail
            ? 'Ontraccr failed to send a scheduled reminder email'
            : 'Scheduled reminder email was sent';
        } else {
          stepTitle = didFail
            ? `Ontraccr failed to ${failVerb} an email`
            : `Form was ${successVerb} in an email`;
        }
        let color = didFail ? 'red' : 'black';
        let textColor = didFail ? 'red' : null;
        if (pending) {
          color = 'gray';
          textColor = 'gray';
          stepTitle = isScheduledReminder
            ? 'Ontraccr is preparing to send a scheduled reminder email'
            : `Ontraccr is preparing to ${failVerb} an email`;
        }

        const emailDescription = isScheduledReminder && !didFail && !pending
          ? ' sent a scheduled reminder email to'
          : ' sent a PDF version of the form in an email to';

        const isScheduleCreationStep = isScheduledReminder && scheduleId && !emailList?.length;

        let stepAction;

        if (!pending) { // pending
          if (isScheduleCreationStep) { // schedule reminder email
            const cancellerName = cancelledByUserId ? userMap[cancelledByUserId]?.name : null;
            stepAction = {
              onClick: onCancelEmailReminder(scheduleId),
              title: scheduleCancelled ? getCancelledTitle(cancelReason, cancellerName) : 'Stop Reminders',
              loading: cancellingSchedule === scheduleId,
              disabled: scheduleCancelled,
            };
          } else if (!isScheduledReminder) { // regular email step - resend
            stepAction = {
              onClick: onEmailSubmit({
                sent,
                stepKey,
                stepId,
                id: workflowStepId,
              }),
              title,
              loading: loadingStep === stepKey,
            };
          }
        }

        let stepText = null;
        if (didFail && errorMessage) {
          stepText = (
            <div style={{ color: 'red' }}>
              {errorMessage}
            </div>
          );
        } else if (!didFail && !pending && (emailList?.length || !isScheduledReminder)) {
          stepText = sentMessage({
            text: emailDescription,
            names: (
              <div>
                { emailList.map(getEmailList) }
                { !!ccList.length && (
                  <div>
                    CC:
                    <br />
                    { ccList.map(getEmailList) }
                  </div>
                )}
                { !!bccList.length && (
                  <div>
                    BCC:
                    <br />
                    { bccList.map(getEmailList) }
                  </div>
                )}
              </div>
            ),
          });
        }

        return (
          <Step
            key={stepKey}
            Icon={isScheduledReminder ? CalendarOutlined : MailOutlined}
            title={stepTitle}
            color={color}
            textColor={textColor}
            text={stepText}
            timestamp={timestamp}
            action={stepAction}
          />
        );
      }
      case 'formUpdate': {
        const {
          templateId,
          formToUpdateTemplateId,
          userId: callerId,
          shouldOverrideForm,
          oldData,
          newData,
        } = jsonMetadata;

        const {
          [callerId]: {
            name: userName = '',
          } = {},
        } = userMap;

        const {
          [templateId]: {
            name: templateName = '',
          } = {},
          [formToUpdateTemplateId]: {
            name: updatedTemplateName = '',
          } = {},
        } = templates;

        return (
          <Step
            key={index}
            Icon={FormOutlined}
            title="Form Update"
            text={
              sentMessage({
                text: (
                  <span>
                    &nbsp;submitted&nbsp;
                    {bold(templateName)}
                    form
                    {shouldOverrideForm ? ' overriding the data in ' : ' appending data to '}
                    form&nbsp;
                    {bold(updatedTemplateName)}
                  </span>
                ),
                caller: userName,
              })
            }
            timestamp={timestamp}
          >
            <FormUpdateStep
              oldData={oldData}
              newData={newData}
            />
          </Step>
        );
      }
      case 'customCobraPMA':
      case 'formTrigger': {
        const {
          userIds: metadataUsers = [],
          sharedForm,
          templateId,
          projectId,
          failed,
          errorMessage,
        } = jsonMetadata;
        const userNames = parseUsers({
          nodeMap,
          stepId,
          userMap,
          metadataUsers,
          formResponseMap,
          defaultValue: (positionId) => {
            if (!(positionId in positionIdMap)) return null;
            return <>
              - All {positionIdMap[positionId].name} users
              <br />
            </>;
          },
        });
        const {
          [stepId]: {
            data: {
              form,
            } = {},
          } = {},
        } = nodeMap;

        const {
          [projectId]: {
            name: projectName = '',
          } = {},
        } = projectMap;

        // Unfortunately we cannot use the templateId for everything because past forms
        // Will not have this in the metadata
        const relevantTemplateId = form?.startsWith('field-')
          ? templateId
          : form;

        const defaultValue = sharedForm ? '' : 'a';
        const {
          [relevantTemplateId]: {
            name: templateName = defaultValue,
          } = {},
        } = templates;

        const projectPart = projectName ? bold(` for ${projectName} `) : ' ';

        return (
          <Step
            key={index}
            Icon={FormOutlined}
            title={failed ? 'Failed to trigger form' : 'Form Trigger'}
            color={failed ? 'red' : 'black'}
            textColor={failed ? 'red' : null}
            text={
              failed
                ? errorMessage
                : sentMessage({
                  text: <span> initiated {sharedForm ? 'a shared' : ''} {bold(templateName)} form{projectPart}{userNames.length ? 'for' : ''} </span>,
                  names: userNames,
                })
            }
            timestamp={timestamp}
          />
        );
      }
      case 'dispatch': {
        const {
          [stepId]: {
            data: {
              fieldMappings = {},
            } = {},
          } = {},
        } = nodeMap;
        const {
          title: titleFieldId,
          userIds: userFieldId,
        } = fieldMappings;
        const {
          [titleFieldId]: {
            value: shiftTitle = '',
          } = {},
          [userFieldId]: {
            values: users = [],
          } = {},
        } = formResponseMap;

        const {
          multi,
          shiftTitles = [shiftTitle],
        } = jsonMetadata;

        const safeShiftTitles = shiftTitles ?? [shiftTitle];
        const { userIds: metadataUsers } = jsonMetadata;
        const ourUsers = (
          metadataUsers
            ? metadataUsers.map((mId) => userMap[mId])
            : users
        );
        const numShifts = safeShiftTitles.length;
        const suffix = numShifts === 1 ? ' was' : 's were';
        const stepTitlePrefix = multi ? numShifts : 'A';

        const namesList = multi && numShifts > 1
          ? shiftTitles
          : ourUsers.map((user) => user.name);
        const text = multi && numShifts > 1
          ? <span> scheduled the following shifts: </span>
          : <span> scheduled a {bold(shiftTitle)} shift for </span>;

        return (
          <Step
            key={index}
            Icon={CalendarOutlined}
            title={`${stepTitlePrefix} Shift${suffix} scheduled`}
            text={
              sentMessage({
                text,
                names: namesList.map((name) => (
                  <>
                  {`- ${name}`}
                  <br />
                  </>
                )),
              })
            }
            timestamp={timestamp}
          />
        );
      }
      case 'updateContract': {
        const {
          sovLineItemCount,
          autoSubmittedInvoice,
          userId: metadataUserId,
          projectIds,
        } = jsonMetadata ?? {};
        // show this step if Link SOV table is ON, otherwise show change order step
        if (sovLineItemCount !== undefined) {
          const lineItemText = sovLineItemCount === 1 ? 'line item' : 'line items';
          const invoiceText = autoSubmittedInvoice ? ' and submitted the invoice' : '';
          const {
            [metadataUserId]: { name: submitter = 'Ontraccr' } = {},
          } = userMap;
          const projectLabels = (projectIds ?? [])
            .map((projectId) => {
              const { name, number } = projectMap[projectId] ?? {};
              return formatProjectLabelFromCompanySettings({ name, number, settings });
            })
            .filter(Boolean);
          const projectText = projectLabels.length ? ` for ${projectLabels.join(', ')}` : '';
          return (
            <Step
              key={index}
              Icon={DollarOutlined}
              title="Contract was updated"
              text={(
                <div>
                  {bold(submitter)} updated {sovLineItemCount} schedule of values {lineItemText}{projectText}{invoiceText}.
                </div>
              )}
              timestamp={timestamp}
            />
          );
        }
        const {
          [stepId]: {
            data: {
              fieldMappings = {},
            } = {},
          } = {},
        } = nodeMap;
        const {
          contractAmount: contractAmountFieldId,
          description: descriptionFieldId,
        } = fieldMappings;
        const {
          [contractAmountFieldId]: {
            value: changeOrderContractAmount = 0,
          } = {},
          [descriptionFieldId]: {
            value: changeOrderDescription = '',
          } = {},
        } = formResponseMap;
        return (
          <Step
            key={index}
            Icon={DollarOutlined}
            title="Change Order was created"
            text={(
              <div>
                {Ontraccr} created a change order: {changeOrderDescription} with contract amount: {currencyFormatter(changeOrderContractAmount)}
              </div>
            )}
            timestamp={timestamp}
          />
        );
      }
      case 'createContract': {
        const {
          [stepId]: {
            data: {
              projectFieldId,
            } = {},
          } = {},
        } = nodeMap;

        const { sovLineItemCount } = jsonMetadata;

        const projectResponse = formResponseMap[projectFieldId]?.values?.[0];
        const projectName = projectResponse?.name ? ` for ${projectResponse?.name}` : '';
        const text = sovLineItemCount === 1 ? 'line item' : 'line items';
        return (
          <Step
            key={index}
            Icon={TableOutlined}
            title="Contract was created"
            text={(
              <div>
                {Ontraccr} added {sovLineItemCount} schedule of values {text}{projectName}.
              </div>
            )}
            timestamp={timestamp}
          />
        );
      }
      case 'status': {
        const { metadataUserName, metadataStatusId } = jsonMetadata;
        const {
          [stepId]: {
            data: {
              statusId,
            } = {},
          } = {},
        } = nodeMap;
        const {
          [metadataStatusId || statusId]: { status } = {},
        } = formStatuses;
        return (
          <Step
            key={index}
            Icon={TagOutlined}
            title="Status was updated"
            text={
              <div>
                {metadataUserName || 'Ontraccr'} updated the status of the form {status ? ' to ' : ''} {bold(toTitleCase(status))}
              </div>
            }
            timestamp={timestamp}
          />
        );
      }
      case 'timeDelay': {
        const {
          pending = false,
          runAt,
          resumed = false,
          resumedImmediately = false,
          failed = false,
          reason,
        } = jsonMetadata;

        let title;
        let text = null;
        let color = 'black';
        let textColor = null;

        if (failed) {
          title = 'Time delay failed';
          color = 'red';
          textColor = 'red';
          text = (
            <div>
              {Ontraccr} could not schedule the time delay
              {reason === 'missing_anchor'
                ? ' because required date information was missing.'
                : '.'}
            </div>
          );
        } else if (resumed) {
          title = 'Time delay completed';
          text = (
            <div>
              {Ontraccr} resumed the workflow after the configured time delay.
            </div>
          );
        } else if (resumedImmediately) {
          title = 'Time delay skipped';
          text = (
            <div>
              {Ontraccr} continued the workflow immediately because the delay end time had already passed
              {runAt ? ` (${formatTimelineDate(runAt)}).` : '.'}
            </div>
          );
        } else if (pending) {
          title = 'Workflow paused for time delay';
          color = 'gray';
          textColor = 'gray';
          text = (
            <div>
              {Ontraccr} paused the workflow until {runAt ? formatTimelineDate(runAt) : 'the scheduled time'}.
            </div>
          );
        } else {
          title = 'Time delay';
        }

        return (
          <Step
            key={index}
            Icon={ClockCircleOutlined}
            title={title}
            color={color}
            textColor={textColor}
            text={text}
            timestamp={timestamp}
          />
        );
      }
      case 'edit': {
        const userNames = parseUsers({
          formResponseMap,
          nodeMap,
          stepId,
          userMap,
          defaultValue: (positionId) => {
            if (!(positionId in positionIdMap)) return null;
            return <>
              - All {positionIdMap[positionId].name} users
              <br />
            </>;
          },
        });
        return (
          <Step
            key={index}
            Icon={EditOutlined}
            title="Form Edit Requested"
            text={
              sentMessage({
                text: 'sent the form to the following users for edits:',
                names: userNames,
              })
            }
            timestamp={timestamp}
          />
        );
      }
      case 'edited': {
        const { editorId } = JSON.parse(metadata);
        return (
          <Step
            key={index}
            Icon={EditFilled}
            title="Form Edited"
            text={(
              <div>
                Form edited {userMap[editorId] ? `by ${userMap[editorId].name}` : ''}
              </div>
            )}
            timestamp={timestamp}
          />
        );
      }
      case 'qbInvoice':
      case 'qbPO': {
        let tax;
        if (name === 'qbInvoice' && metadata) {
          const { taxName } = JSON.parse(metadata);
          tax = taxName;
        }
        const taxStr = tax ? `with tax code ${tax}` : '';
        return (
          <Step
            key={index}
            Icon={CloudSyncOutlined}
            title="Invoice was synced with QuickBooks"
            text={(
              <div>
                {Ontraccr} synced the {name === 'qbInvoice' ? 'Invoice' : 'PO'} data into QuickBooks {taxStr}
              </div>
            )}
            timestamp={timestamp}
          />
        );
      }
      case 'qbInvoicePaid': {
        return (
          <Step
            key={index}
            Icon={DollarOutlined}
            title="Invoice was marked as paid in QuickBooks"
            timestamp={timestamp}
          />
        );
      }
      case 'ontraccrInvoicePaid': {
        return (
          <Step
            key={index}
            Icon={DollarOutlined}
            title="Invoice was marked as paid in Ontraccr"
            text={
              <div>
                {Ontraccr} updated the invoice data in QuickBooks
              </div>
            }
            timestamp={timestamp}
          />
        );
      }
      case 'stripePayment': {
        const {
          blocked: paymentEmailBlocked,
          reason: paymentEmailBlockReason,
        } = getEmailBlockFromMetadata(jsonMetadata);
        if (paymentEmailBlocked) {
          return renderEmailBlockedTimeline({
            stepKey: index,
            title: 'Payment link was not sent',
            Icon: DollarOutlined,
            timestamp,
            reason: paymentEmailBlockReason,
          });
        }
        const { email } = jsonMetadata;
        const emailText = email ? ` to ${email}` : '';
        return (
          <Step
            key={index}
            Icon={DollarOutlined}
            title="Payment link was sent"
            text={
              <div>
                {Ontraccr} sent a payment link{emailText}
              </div>
            }
            timestamp={timestamp}
          />
        );
      }
      case 'stripePaymentPaid': {
        return (
          <Step
            key={index}
            Icon={DollarOutlined}
            title="Payment received"
            timestamp={timestamp}
          />
        );
      }
      case 'materialDebit': {
        return (
          <Step
            key={index}
            Icon={GoldOutlined}
            title="Materials Updated"
            text={
              <span>{Ontraccr} updated materials in your materials database</span>
            }
            timestamp={timestamp}
          />
        );
      }
      case 'updateEquipment': {
        return (
          <Step
            key={index}
            Icon={ToolOutlined}
            title="Equipment Updated"
            text={
              <span>{Ontraccr} updated equipment in your equipment database</span>
            }
            timestamp={timestamp}
          />
        );
      }
      case 'cancel': {
        const { userId: cancelUser } = jsonMetadata;
        const userText = cancelUser in userMap ? ` by ${userMap[cancelUser].name}`: '';
        return (
          <Step
            key={index}
            Icon={StopOutlined}
            color="red"
            title={`Workflow was cancelled${userText}`}
            timestamp={timestamp}
            prefixCls="ot-timeline-cancel"
          />
        );
      }
      case 'logicalCondition':
      case 'logicalYesNo': {
        const { logicalValue, fieldName, sectionName, hasChild } = jsonMetadata;
        const title = name === 'logicalCondition' ? 'Logical Condition' : 'Logical Yes/No';

        return (
          <Step
            key={index}
            Icon={BranchesOutlined}
            title={`${title} block was evaluated`}
            text={
              <div>
                {bold(sectionName)} - {bold(fieldName)} was evalulated and the result was {bold(logicalValue || 'no action')}.
                {hasChild ? 'The next step was triggered.' : ''}
              </div>
            }
            timestamp={timestamp}
          />
        );
      }
      case 'logicalLoop': {
        const { valuesToLoop = [], fieldName = 'field', sectionName = 'section' } = jsonMetadata;
        return (
          <Step
            key={index}
            Icon={RetweetOutlined}
            title="Logical Loop block was evaluated"
            text={
              <div>
                {bold(sectionName)} - {bold(fieldName)} was evalulated and was looped: {bold(valuesToLoop.length)} times.
                { valuesToLoop.length ? 'The next step was triggered on the following values:' : ''}
                { valuesToLoop.map(({ id, name }) => (
                  <div key={id}>
                    {`- ${name}`}
                    <br />
                  </div>
                ))}
              </div>
            }
            timestamp={timestamp}
          />
        );
      }
      case 'reassign': {
        const { userId, addedUsers, removedUsers } = jsonMetadata;
        const userText = userId in userMap ? bold(userMap[userId].name): '';

        return (
          <Step
            key={index}
            Icon={BranchesOutlined}
            title="Form Reassigned"
            timestamp={timestamp}
            text={
              sentMessage({
                caller: userText,
                text: 'reassigned the form',
                names: (
                  <div>
                    { !!addedUsers.length && (
                      <div>
                        Added:
                        <br />
                        { formatUserIds({ userIds: addedUsers, userMap }) }
                      </div>
                    )}
                    { !!removedUsers.length && (
                      <div>
                        Removed:
                        <br />
                        { formatUserIds({ userIds: removedUsers, userMap }) }
                      </div>
                    )}
                  </div>
                ),
              })
            }
          />
        );
      }
      case 'createProfile': {
        const { subType } = jsonMetadata;
        return (
          <Step
            key={index}
            Icon={ProjectFilled}
            title="Create Project"
            timestamp={timestamp}
            text={
              <div>A {subType} Profile was created</div>
            }
          />
        );
      }
      case 'createBoardCard': {
        const {
          status,
          board,
          cardTitle,
          id: cardId,
          boardId,
          parentCard,
        } = jsonMetadata;
        const {
          cardId: parentCardId,
          cardTitle: parentCardTitle,
          boardId: parentBoardId,
        } = parentCard ?? {};
        const parentCardText = parentCard ? (
          <span>triggered by card
            <FormTimelineCardLink
              cardTitle={parentCardTitle}
              cardId={parentCardId}
              boardId={parentBoardId}
              onCardLinkClick={onCardLinkClick}
            />
          </span>
        ) : '';
        const prefix = cardTitle ? ' and status ' : 'With status';
        const statusText = status && board ? <span>{prefix} {statusTitle(board, status)}</span> : '';
        const cardTitleText = cardTitle
          ? (
            <span>With title
              <FormTimelineCardLink
                cardTitle={cardTitle}
                cardId={cardId}
                boardId={boardId}
                onCardLinkClick={onCardLinkClick}
              />
            </span>
          )
          : '';
        return (
          <Step
            key={index}
            Icon={GroupOutlined}
            title="Board Card Created"
            timestamp={timestamp}
            text={
              <div>{cardTitleText}{statusText}{parentCardText}</div>
            }
          />
        );
      }
      case 'createChildCard': {
        const {
          cardTitle: childCardTitle,
          id: childCardId,
          boardId: childCardBoardId,
          status,
          board,
        } = jsonMetadata;
        return (
          <Step
            key={index}
            Icon={GroupOutlined}
            title="Child Board Card Created"
            timestamp={timestamp}
            text={(
              <div>
                {Ontraccr} ran the form workflow,
                which triggered a child form that created a board card
                <FormTimelineCardLink
                  cardTitle={childCardTitle}
                  cardId={childCardId}
                  boardId={childCardBoardId}
                  onCardLinkClick={onCardLinkClick}
                /> with status {statusTitle(board, status)}.
              </div>
            )}
          />
        );
      }
      case 'photoRotation': {
        const { imageName, userName: userNameRotate } = jsonMetadata;
        return (
          <Step
            key={index}
            Icon={RotateRightOutlined}
            title="Photo Rotated"
            timestamp={timestamp}
            text={
              <div>{`${imageName} rotated by ${userNameRotate}`}</div>
            }
          />
        );
      }
      case 'updateBoardCard': {
        const {
          status, board, cardIdsToUpdate = [], pending,
        } = jsonMetadata;
        const cardsText = ` ${cardIdsToUpdate.length} ${cardIdsToUpdate.length === 1 ? 'card ' : 'cards '}`;
        const updateText = (
          <span>
            Updated
            {cardsText}
            {status && board ? (
              <span>
                {'with status '}
                {statusTitle(board, status)}
              </span>
            ) : ''}
          </span>
        );
        return (
          <Step
            key={index}
            Icon={GroupOutlined}
            title={
              pending
                ? 'Board Card Update Pending'
                : 'Board Card Updated'
            }
            color={pending ? 'gray' : 'black'}
            textColor={pending ? 'gray' : null}
            timestamp={timestamp}
            text={
              !pending ? <div>{updateText}</div> : null
            }
          />
        );
      }
      case 'updateCost': {
        const {
          projectId,
          projects: costedProjects,
          subContractProjectId,
          isBudgetUpdate,
        } = jsonMetadata;
        const skipped = jsonMetadata.skipped ?? {};
        const pageName = isBudgetUpdate ? 'Budget' : 'Progress';
        const getProjectLabel = (id) => {
          const { name: projectName, number } = projectMap?.[id] ?? {};
          return formatProjectLabelFromCompanySettings({
            name: projectName,
            number,
            settings,
          }) ?? 'Unknown';
        };

        // metadata written before per-project costing only has the project ids
        if (!costedProjects) {
          return (
            <Step
              key={index}
              Icon={DollarOutlined}
              title="Project Cost Updated"
              timestamp={timestamp}
              text={(
                <div>
                  {`Updated ${getProjectLabel(projectId)}'s ${pageName} Page`}
                </div>
              )}
            />
          );
        }

        const skippedText = [
          [skipped.timeEntryNoProject, (count) => `${count} time entry ${pluralize(count, 'row')} with no project`],
          [skipped.noProjectDropdown, (count) => `${count} ${pluralize(count, 'row')} because the Project Dropdown was empty or not set`],
          [skipped.subContractNoProject, () => 'the sub-contract change order because the Project Dropdown was empty'],
          [skipped.costcodeNotOnProject, (count) => `${count} ${pluralize(count, 'row')} whose cost code is on another project`],
          [skipped.gridGlobalCostcode, (count) => `${count} equipment grid ${pluralize(count, 'cell')} on a global cost code`],
          [skipped.gridNoProject, (count) => `${count} equipment grid ${pluralize(count, 'cell')} with no project`],
          [skipped.gridEquipmentNoRate, (count) => `${count} equipment grid ${pluralize(count, 'cell')} with no hourly cost`],
        ]
          .filter(([count]) => count > 0)
          .map(([count, getText]) => getText(count))
          .join(', ');

        const skippedLine = skippedText && (
          <div>
            <WarningOutlined style={{ color: Colors.ONTRACCR_DARK_YELLOW, marginRight: 5 }} />
            {`Skipped ${skippedText}`}
          </div>
        );

        const subContractLine = subContractProjectId && (
          <div>{`Created a sub-contract change order on ${getProjectLabel(subContractProjectId)}`}</div>
        );

        if (!costedProjects.length && !subContractProjectId) {
          return (
            <Step
              key={index}
              Icon={DollarOutlined}
              title="No Project Costs Updated"
              color="gray"
              textColor="gray"
              timestamp={timestamp}
              text={skippedLine || 'No costable rows were submitted'}
            />
          );
        }

        const projectCount = costedProjects.length;

        return (
          <Step
            key={index}
            Icon={DollarOutlined}
            title="Project Costs Updated"
            timestamp={timestamp}
            text={(
              <div>
                {projectCount > 0 && (
                  <>
                    <div>
                      {Ontraccr}
                      {`updated the ${pageName} page for ${projectCount} ${pluralize(projectCount, 'project')}:`}
                    </div>
                    {costedProjects.map(({
                      projectId: costedProjectId,
                      total,
                      hours,
                      entryCount,
                    }) => (
                      <div key={costedProjectId}>
                        {'- '}
                        {bold(`${getProjectLabel(costedProjectId)}:`)}
                        {currencyFormatter(total, false)}
                        {hours > 0 ? `, ${Math.round(hours * 100) / 100} h` : ''}
                        {` from ${entryCount} ${pluralize(entryCount, 'entry', 'entries')}`}
                      </div>
                    ))}
                  </>
                )}
                {subContractLine}
                {skippedLine}
              </div>
            )}
          />
        );
      }
      case 'externalSignature': {
        const {
          failed,
          signers,
          errorMessage,
        } = jsonMetadata;
        const stepKey = `${stepId}-${timestamp}`;

        const stepTitle = failed
          ? 'Ontraccr failed to create the Docusign envelope'
          : 'Envelope was created in Docusign';
        return (
          <Step
            key={stepKey}
            Icon={MailOutlined}
            title={stepTitle}
            color={failed ? 'red' : 'black'}
            textColor={failed ? 'red' : null}
            text={
              failed
                ? errorMessage
                : sentMessage({
                  caller: bold('Docusign'),
                  text: ' sent an envelope for signing to:',
                  names: signers
                    .sort(((a, b) => a.order - b.order))
                    .map(({ email, order }) => getEmailList(email, order)),
                })
            }
            timestamp={timestamp}
          />
        );
      }
      case 'createTime': {
        const {
          autoSubmit,
          autoApprove,
          errors: {
            conflictErrors = [],
            parsedInsertErrors = [],
            parsingErrors = [],
          } = {},
        } = jsonMetadata;
        const hasSubtext = autoSubmit || autoApprove;
        const autoApproveText = ' and approved';
        const subtext = `submitted${autoApprove ? autoApproveText : ''}`;
        const tableErrors = parsingErrors?.filter((err) => err?.isTimeEntryTableError)?.map((err) => err.message);
        const nativeError = parsingErrors?.filter((err) => err?.data?.type === 'native')?.[0] ?? null; // this can only contain one error
        const hasErrors = (
          !!conflictErrors?.length
          || !!parsedInsertErrors?.length
          || !!nativeError
          || !!tableErrors?.length
        );
        const subText = hasSubtext
          ? (
            <div>
              {Ontraccr} automatically {subtext} the time entries
            </div>
          )
          : null;
        const combinedErrors = [...(parsedInsertErrors ?? []), ...(tableErrors ?? [])];
        const errorText = hasErrors
          ? (
            <>
              { !!conflictErrors?.length && (
                <div style={{ color: 'red', marginTop: subText ? 5 : 0, fontFamily: 'roboto-regular' }}>
                  The following entries conflicted with existing time entries and were not created:
                  {
                    conflictErrors.map((err, errIndex) => (
                      <div style={{ marginLeft: 5 }}>
                        {'- '}{err}
                        {errIndex < conflictErrors.length - 1 && <br />}
                      </div>
                    ))
                  }
                </div>
              )}
              { !!combinedErrors?.length && (
                <div style={{ color: 'red', marginTop: subText ? 5 : 0, fontFamily: 'roboto-regular' }}>
                  The following time entries were not created due to errors:
                  {
                    combinedErrors.map((err, errIndex) => (
                      <div style={{ marginLeft: 5 }}>
                        {'- '}{err}
                        {errIndex < combinedErrors.length - 1 && <br />}
                      </div>
                    ))
                  }
                </div>
              )}
              {!!nativeError && (
                <div style={{ color: 'red', marginTop: subText ? 5 : 0, fontFamily: 'roboto-regular' }}>
                  A time entry was not created due to the following error:
                  <div style={{ marginLeft: 5 }}>
                    {'- '}{nativeError.message}
                  </div>
                </div>
              )}
            </>
          ) : null;
        return (
          <Step
            key={index}
            Icon={ClockCircleOutlined}
            title="Time entries created"
            timestamp={timestamp}
            text={(
              <>
                {subText}
                {errorText}
              </>
            )}
          />
        );
      }
      case 'createSubtask': {
        const { taskIds } = jsonMetadata;
        return (
          <Step
            key={index}
            Icon={ProfileOutlined}
            title="Tasks Created"
            timestamp={timestamp}
            text={
              <div>{taskIds?.length || 0} tasks created</div>
            }
          />
        );
      }
      case 'eclipse': {
        const { errors } = jsonMetadata;
        const didFail = errors?.length > 0;
        const stepTitle = didFail
          ? 'Failed to sync to Eclipse'
          : 'Form was sycned to Eclipse';
        return (
          <Step
            key={index}
            Icon={CloudSyncOutlined}
            title={stepTitle}
            color={didFail ? 'red' : 'black'}
            textColor={didFail ? 'red' : null}
            timestamp={timestamp}
            text={
              didFail
                ? errors.map((err) => (<div style={{ color: 'red' }}>{err}</div>))
                : null
            }
          />
        );
      }
      case 'sageIntacctPO': {
        const { errors = [], createdPOs = [] } = jsonMetadata;
        const didFail = errors?.length > 0;
        const stepTitle = didFail
          ? 'Failed to sync PO to Sage Intacct'
          : 'PO synced to Sage Intacct';
        let stepText = null;
        if (didFail && errors.length) {
          stepText = errors.map((err) => (<div style={{ color: 'red' }} key={err}>{err}</div>));
        } else if (createdPOs?.length > 0) {
          stepText = <div>{createdPOs.length} purchase order(s) created in Sage Intacct</div>;
        }
        return (
          <Step
            key={index}
            Icon={CloudSyncOutlined}
            title={stepTitle}
            color={didFail ? 'red' : 'black'}
            textColor={didFail ? 'red' : null}
            timestamp={timestamp}
            text={stepText}
          />
        );
      }
      case 'sageIntacctCreateJournal': {
        const { errors = [], createdJournals = [] } = jsonMetadata;
        const didFail = errors?.length > 0;
        const stepTitle = didFail
          ? 'Failed to sync journal to Sage Intacct'
          : 'Journal synced to Sage Intacct';
        let stepText = null;
        if (didFail && errors.length) {
          stepText = errors.map((err) => (<div style={{ color: 'red' }} key={err}>{err}</div>));
        } else if (createdJournals?.length > 0) {
          stepText = <div>{createdJournals.length} journal entry(ies) created in Sage Intacct</div>;
        }
        return (
          <Step
            key={index}
            Icon={CloudSyncOutlined}
            title={stepTitle}
            color={didFail ? 'red' : 'black'}
            textColor={didFail ? 'red' : null}
            timestamp={timestamp}
            text={stepText}
          />
        );
      }
      case 'createCertification': {
        const {
          userIds: certUserIds = [],
          userId: legacyCertUserId,
          error: errorText,
        } = jsonMetadata;
        const resolvedCertUserIds = certUserIds.length
          ? certUserIds
          : [legacyCertUserId];
        const certifiedUsers = formatListWithAnd(
          resolvedCertUserIds.map((id) => userMap[id]?.name ?? 'Unknown'),
          'Unknown',
        );
        const stepTitle = errorText ? 'Failed to create certification' : 'Certification Created';
        const stepText = errorText
          ? <div style={{ color: 'red' }}>{errorText}</div>
          : <div>{`A certification was created for ${certifiedUsers}`}</div>;
        const color = errorText ? 'red' : 'black';
        const textColor = errorText ? 'red' : null;
        return (
          <Step
            key={index}
            Icon={FileTextOutlined}
            title={stepTitle}
            timestamp={timestamp}
            text={stepText}
            color={color}
            textColor={textColor}
          />
        );
      }
      case 'createUser': {
        const {
          createdUserId,
          createdUserName,
          failed = false,
          errorMessage,
          errorType,
        } = jsonMetadata;
        const {
          blocked: welcomeEmailBlocked,
          reason: welcomeEmailBlockReason,
        } = getEmailBlockFromMetadata(jsonMetadata);

        // Full Name
        const userName = createdUserName || userMap[createdUserId]?.name || 'Unknown User';

        const stepTitle = failed
          ? 'Failed to create user'
          : 'User Created';

        const color = failed ? 'red' : 'black';
        const textColor = failed ? 'red' : null;

        return (
          <Step
            key={index}
            Icon={UserOutlined}
            title={stepTitle}
            color={color}
            textColor={textColor}
            timestamp={timestamp}
            text={
              failed
                ? (
                  <div style={{ color: 'red' }}>
                    {errorMessage || 'User creation failed due to validation errors'}
                    {errorType && (
                      <div style={{ marginTop: 5, fontSize: '12px', color: '#666' }}>
                        Error type: {errorType}
                      </div>
                    )}
                  </div>
                )
                : (
                  <div>
                    {`Created user: ${userName}`}
                    {welcomeEmailBlocked && renderEmailBlockedTimeline({
                      asStep: false,
                      reason: welcomeEmailBlockReason,
                      textKind: 'welcome',
                    })}
                  </div>
                )
            }
          />
        );
      }
      case 'createPayableInvoice': {
        const {
          failed = false,
          errorMessage,
          invoiceNumber,
          amount,
        } = jsonMetadata;

        const stepTitle = failed
          ? 'Failed to create payable invoice'
          : 'Payable invoice created';

        return (
          <Step
            key={index}
            Icon={DollarOutlined}
            title={stepTitle}
            color={failed ? 'red' : 'black'}
            textColor={failed ? 'red' : null}
            timestamp={timestamp}
            text={
              failed
                ? <div style={{ color: 'red' }}>{errorMessage}</div>
                : `Payable invoice ${invoiceNumber} created for $${amount}`
            }
          />
        );
      }
      default:
        // Shouldnt reach this
        return (
          <Step
            key={index}
            Icon={MailOutlined}
            title={name}
            timestamp={timestamp}
          />
        );
    }
  }, [
    userName,
    userMap,
    projectMap,
    settings,
    nodeMap,
    dispatch,
    loadingStep,
    onEmailSubmit,
    isExternalForm,
    onCardLinkClick,
    formResponseMap,
    formType,
  ]);

  const assignedNameView = useMemo(() => (
    assignedUserNames.map((assignedName) => (
      <>
        {`- ${assignedName}`}
        <br />
      </>
    ))
  ), [assignedUserNames]);

  const hasAssigned = useMemo(() => (
    steps.some((step) => step?.name === 'assign')
  ), [sortedSteps]);

  return (
    <div className="form-timeline-container">
      <Timeline
        style={{ paddingLeft: 20, paddingTop: 10 }}
        pending={<PendingView isFinished={isFinished} />}
        pendingDot={<PendingDot isFinished={isFinished} />}
      >
        {
          !hasAssigned
          && (
          <Timeline.Item
            color="black"
          >
            <TimelineStepText
              title="Workflow Trigger"
              timestamp={createdAt}
              text={
                sentMessage({
                  names: isExternalForm ? 'External User' : assignedNameView,
                  text: 'assigned the form to: ',
                })
              }
            />
          </Timeline.Item>
          )
        }
        {sortedSteps.map(parseStep)}
        {
        formState === 'Cancelled'
          ? null
          : <CompleteStep isFinished={isFinished} />
        }
      </Timeline>
    </div>
  );
}
